<template>
  <div class="san-instant-search fit">
    <slot
      :search-client="searchClient"
      :instant-search-instance="instantSearchInstance"
    />
  </div>
</template>

<script>
const cache = new Set()
const warn = message => {
  if (cache.has(message)) return
  cache.add(message)
  console.warn(message)
}
const oldApiWarning = `Vue InstantSearch: You used the prop api-key or app-id.
These have been replaced by search-client.

See more info here: https://www.algolia.com/doc/api-reference/widgets/instantsearch/vue/#widget-param-search-client`
</script>

<script setup>
import { toRef, ref, watch, onMounted, nextTick, onBeforeUnmount, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useExploreStore } from '@/stores'
import instantsearch from 'instantsearch.js/es'
import { INSTANTSEARCH_FUTURE_DEFAULTS } from 'instantsearch.js/es/lib/InstantSearch'
import TISAdapter from 'typesense-instantsearch-adapter/src/TypesenseInstantsearchAdapter'
import { isEmpty } from 'lodash'

defineOptions({ name: 'InstantSearch' })

const props = defineProps ({
  insightsClient: { type: Function, default: undefined },
  indexName: { type: String, required: true },
  compositionID: { type: String, default: undefined },
  insights: {
    default: undefined,
    validator: value => typeof value === 'undefined' || typeof value === 'boolean' || typeof value === 'object'
  },
  stalledSearchDelay: { type: Number, default: undefined },
  onStateChange: { type: Function, default: undefined },
  initialUiState: { type: Object, default: undefined },
  apiKey: {
    type: String,
    default: undefined,
    validator: value => {
      if (value) warn(oldApiWarning)
      return false
    }
  },
  appId: {
    type: String,
    default: undefined,
    validator: value => {
      if (value) warn(oldApiWarning)
      return false
    }
  },
  middlewares: { type: Array, default: null },
  future: { type: Object, default: () => ({}) },
  searchParams: { type: Object, default: () => {} },
  minQueryLength: { type: Number, default: 0 },
  // false for a detached instance (e.g. WatchlistCriteriaFilters.vue) that must not read from
  // or write to the current page's URL — instantsearch.js treats routing: undefined as "no
  // router middleware", so this is skipped entirely rather than passed with empty handlers.
  routingEnabled: { type: Boolean, default: true }
})

const $route = useRoute()
const $router = useRouter()
const searchParameters = toRef(useExploreStore(), 'searchParameters') // eslint-disable-line

const typesenseServer = {
  apiKey: import.meta.env.TYPESENSE_API_KEY,
  nodes: [
    {
      host: import.meta.env.TYPESENSE_HOST,
      port: import.meta.env.TYPESENSE_PORT,
      protocol: import.meta.env.TYPESENSE_PROTOCOL
    }
  ],
  cacheSearchResultsForSeconds: 2 * 60
}

const buildAdapterConfig = searchParams => ({
  server: typesenseServer,
  additionalSearchParameters: {
    q: '',
    ...searchParams
  }
})

const typesenseAdapter = new TISAdapter(buildAdapterConfig(props.searchParams))

const searchClient = {
  ...typesenseAdapter.searchClient,
  search: async reqs => {
    // console.log('%csearchClient', 'color:lime', reqs)
    // Helper to check for refinements
    const hasRefinements = params => {
      const check = val =>
        Array.isArray(val)
          ? val.some(check)
          : !!val && val !== '' && val.split(':')[1] !== 'false'

      return (
        check(params.facetFilters)
        || check(params.filters)
        || check(params.numericFilters)
        || check(params.tagFilters)
      )
    }

    // Check if all queries are empty and there are no refinements
    const hasSearch = reqs.some(({ params }) => {
      const hasQuery = !!params.query?.trim()
      return hasQuery || hasRefinements(params)
    })

    // try {
    const emptyResults = reqs.map(() => ({
      hits: [], nbHits: 0, nbPages: 0, page: 0,
      hitsPerPage: 0, facets: {}, processingTimeMS: 0, query: '', params: ''
    }))

    let res
    if (!hasSearch) {
      const facetReqs = reqs.map(r => ({
        ...r,
        params: { ...r.params, hitsPerPage: 0, page: -1 }
      }))
      const facetRes = await typesenseAdapter.searchClient.search(facetReqs).catch(err => {
        return { results: emptyResults }
      })
      res = { results: facetRes.results.map(r => ({ ...r, nbHits: 0, nbPages: 0 })) }
    } else {
      res = await typesenseAdapter.searchClient.search(reqs).catch(err => {
        return { results: emptyResults }
      })
    }

    return res
    // } catch (err) {
    //   console.error('Typesense search error:', err)
    // }

    // return typesenseAdapter.searchClient.search(reqs)

    // const isEmptyQuery = reqs.every(({ params: { query } }) => !query?.trim())

    // const res = isEmptyQuery
    //   ? await Promise.resolve({
    //       results: reqs.map(() => ({
    //         facets: {},
    //         facetStats: {},
    //         hits: [],
    //         hitsPerPage: 0,
    //         nbHits: 0,
    //         nbPages: 0,
    //         page: 0,
    //         processingTimeMS: 0,
    //         query: '',
    //         renderingContent: {},
    //         exhaustiveNbHits: false,
    //         params: ''
    //       }))
    //     })
    //   : await typesenseAdapter.searchClient.search(reqs)

    // return res
  }
}

// Set to true after IS.start() — blocks spurious empty writes during widget init/unmount
let _isStarted = false

// Flag: set by popstate, cleared by afterEach — ensures cb fires only after $route is updated
let _pendingPopState = false

const routing = {
  router: {
    read: () => $route.query,
    write: query => {
      if (!_isStarted) return
      const cleanQuery = Object.fromEntries(Object.entries(query ?? {}).filter(([, v]) => v != null && v !== ''))
      // If the write would clear the query, only proceed when a SearchBox widget
      // is currently registered. When IS clears the query because the SearchBox
      // widget just unmounted (navigating away from the search page), skip the
      // write so the ?q= param is preserved in the history entry we're leaving.
      if (!cleanQuery.q) {
        const hasSearchBox = instantSearchInstance.value?.mainIndex?.getWidgets()
          .some(w => w.$$type === 'ais.searchBox')
        if (!hasSearchBox) return
      }
      $router.replace({ query: cleanQuery })
    },
    createURL: () => $route.fullPath,
    onUpdate (cb) {
      // Dedup guard — remove stale listener before re-registering
      if (routing.router._onPopState) {
        window.removeEventListener('popstate', routing.router._onPopState)
      }
      // popstate only sets a flag; the actual cb fires in afterEach once $route is updated
      routing.router._onPopState = () => {
        _pendingPopState = true
      }
      window.addEventListener('popstate', routing.router._onPopState)

      routing.router._removeAfterEach?.()
      routing.router._removeAfterEach = $router.afterEach((to, from, failure) => {
        if (_pendingPopState) {
          _pendingPopState = false
          if (!failure) cb(routing.router.read())
        }
      })
    },
    dispose () {
      if (routing.router._onPopState) {
        window.removeEventListener('popstate', routing.router._onPopState)
        routing.router._onPopState = null
      }
      routing.router._removeAfterEach?.()
      routing.router._removeAfterEach = null
      _pendingPopState = false
    }
  },
  stateMapping: {
    stateToRoute: uiState => {
      const state = uiState[props.indexName] || {}
      const route = { q: state.query }
      // Add refinementList to route (e.g. country, atc)
      if (state.refinementList) {
        Object.entries(state.refinementList).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length) {
            route[key.split('.')[0]] = values.join('~')
          }
        })
      }
      // Sync only non-query params to searchParameters (ExploreStore / ais-configure).
      // The 'q' key must NOT go here — passing it to ais-configure causes IS to
      // remove/re-add the configure widget on every keystroke, which schedules a
      // deferred onStateChange(query=undefined) that fires before navigation commits
      // and strips ?q= from the history entry.
      const { q: _q, ...routeFilters } = route
      const prev = searchParameters.value
      if (!prev || Object.keys(routeFilters).some(k => routeFilters[k] !== prev[k]) || Object.keys(prev).some(k => !(k in routeFilters))) {
        searchParameters.value = routeFilters
      }
      return route
    },
    routeToState: routeState => {
      const refinementList = {}
      Object.entries(routeState).forEach(([key, value]) => {
        if (key !== 'q' && value) {
          refinementList[key] = value.split('~')
        }
      })
      // console.log('%crouteToState', 'color:lime', props.searchParams)
      const uiState = !routeState || isEmpty(routeState)
        ? { [props.indexName]: { query: undefined } }
        : {
            [props.indexName]: {
              query: routeState.q
              // refinementList: {
              //   brand: routeState.brands && routeState.brands.split('~')
              // },
              // page: routeState.page // causing error in hits component
            }
          }
      return uiState
    }
  }
}

const onStateChange = ({ uiState, setUiState }) => { // eslint-disable-line
  const { [props.indexName]: { query, ...rest } } = uiState
  if (!query || query.length < props.minQueryLength) {
    // console.log('%conStateChange', 'color:cyan', uiState)
    setUiState({ [props.indexName]: { ...rest, query: '' } })
  } else {
    setUiState(uiState)
  }
  if (props.onStateChange) props.onStateChange({ uiState, setUiState })
}

const instantSearchInstance = ref(null)
const createInstantSearchInstance = () => instantsearch({
  searchClient,
  insightsClient: props.insightsClient,
  insights: props.insights,
  indexName: props.indexName,
  compositionID: props.compositionID,
  routing: props.routingEnabled ? routing : undefined,
  stalledSearchDelay: props.stalledSearchDelay,
  onStateChange,
  initialUiState: props.initialUiState,
  future: {
    preserveSharedStateOnUnmount: true,
    persistHierarchicalRootCount: true,
    ...props.future
  }
})
instantSearchInstance.value = createInstantSearchInstance()

watch(() => props.indexName, val => {
  instantSearchInstance.value.helper.setIndex(val || '').search()
})

watch(() => props.compositionID, val => {
  instantSearchInstance.value.helper
    .setIndex(val || '')
    .search()
})

// This instance stays mounted app-wide (App.vue) and is created with the fallback
// searchParams before any page sets its own, so the adapter must be reconfigured when
// they change — additionalSearchParameters is otherwise read only once at construction.
// refresh() clears the search cache so results fetched under the old query_by aren't reused.
watch(() => props.searchParams, val => {
  typesenseAdapter.updateConfiguration(buildAdapterConfig(val))
  if (instantSearchInstance.value.started) instantSearchInstance.value.refresh()
}, { deep: true })

watch(() => props.stalledSearchDelay, val => {
  // private InstantSearch.js API:
  instantSearchInstance.value._stalledSearchDelay = val
})

watch(() => props.middlewares, (next, prev) => {
  (prev || [])
    .filter(middleware => (next || []).indexOf(middleware) === -1)
    .forEach(middlewareToRemove => {
      instantSearchInstance.value.unuse(middlewareToRemove)
    });

  (next || [])
    .filter(middleware => (prev || []).indexOf(middleware) === -1)
    .forEach(middlewareToAdd => {
      instantSearchInstance.value.use(middlewareToAdd)
    })
}, { immediate: true })

watch(() => props.future, val => {
  instantSearchInstance.value.future = Object.assign(INSTANTSEARCH_FUTURE_DEFAULTS, val)
})

onMounted(async () => {
  await nextTick()
  // On a hard refresh the auth guard holds the initial navigation open, so $route is still
  // the unresolved '/' here. Starting now makes routing.read() miss ?q= (and every other
  // URL param) and the first state write then strips them from the URL.
  await $router.isReady()
  if (!instantSearchInstance.value.started) {
    instantSearchInstance.value.start()
  }
  _isStarted = true
})
onBeforeUnmount(() => {
  _isStarted = false
  if (instantSearchInstance.value.started) instantSearchInstance.value.dispose()

  // a hydrated instance will no longer be hydrated once disposed, and starts from scratch
  instantSearchInstance.value.__initialSearchResults = undefined
})

provide('$_ais_instantSearchInstance', instantSearchInstance)
</script>
